import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";

import { FieldControl } from "../../../shared/components/FieldControl";
import { AsyncMultiAutoComplete } from "../../../shared/components/AsyncMultiAutoComplete";
import { ControlledAutocomplete } from "../../../shared/components";
import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { useInfiniteStakeholders } from "../../projects/hooks/useStakeholders";
import { useSearchProjects } from "../../projects/mutations";
import { usePhase } from "../../projectOverview/hooks/usePhase";

export type OwnerOption = { label: string; value: string };
export type ProjectOption = { label: string; value: string };
export type ProgrammeOption = { label: string; value: string };

export interface ArtefactsFilterValues {
    category: string[];
    subCategory: string[];
    owner: OwnerOption[];
    requiredDate: string | undefined | Date;
    expiryDate: string | undefined | Date;
    // Programme level only
    artefactType: string;
    portfolio: AutocompleteOption[];
    project: ProjectOption[];
    projectPhase: string[];
    programme: ProgrammeOption[];
    // Project level only
    phase: string[];
}

export const emptyArtefactsFilter = (): ArtefactsFilterValues => ({
    category: [],
    subCategory: [],
    owner: [],
    requiredDate: undefined,
    expiryDate: undefined,
    artefactType: "",
    portfolio: [],
    project: [],
    projectPhase: [],
    programme: [],
    phase: [],
});

interface ArtefactsFilterSidebarProps {
    level: "programme" | "project";
    projectId?: string;
    values: ArtefactsFilterValues;
    onApply: (values: ArtefactsFilterValues) => void;
    onClose: () => void;
}

export const ArtefactsFilterSidebar: React.FC<ArtefactsFilterSidebarProps> = ({
    level,
    projectId,
    values,
    onApply,
    onClose,
}) => {
    const { getFilterOptions } = useDisplayField([]);
    const [artefactFilters, setArtefactFilters] = useState<ArtefactsFilterValues>(values);

    useEffect(() => {
        setArtefactFilters(values);
    }, [values]);

    const set = useCallback(<K extends keyof ArtefactsFilterValues>(
        key: K,
        val: ArtefactsFilterValues[K],
    ) => setArtefactFilters((prev) => ({ ...prev, [key]: val })), []);

    const [ownerSearch, setOwnerSearch] = useState("");
    const {
        stakeholders,
        fetchNextPage: fetchNextOwners,
        hasNextPage: hasMoreOwners,
        isFetchingNextPage: isFetchingMoreOwners,
    } = useInfiniteStakeholders({ search: ownerSearch });

    const ownerOptions = useMemo(
        () => stakeholders.map((s) => ({ label: s.full_name, value: String(s.user_id) })),
        [stakeholders],
    );
    const searchProjectsMutation = useSearchProjects();
    const [projectOptions, setProjectOptions] = useState<ProjectOption[]>(artefactFilters.project);

    const handleProjectSearch = useCallback(
        async (term: string) => {
            if (!term?.trim()) {
                setProjectOptions([]);
                return;
            }
            try {
                const res = await searchProjectsMutation.mutateAsync({
                    params: { search_term: term, limit: 50 },
                });
                setProjectOptions(
                    (res?.data || []).map((p) => ({ label: p.title || "", value: String(p.id) })),
                );
            } catch {

            }
        },

        [],
    );
    const { data: phaseData } = usePhase(projectId);
    const [phaseOptions, setPhaseOptions] = useState<{ label: string; value: string }[]>([]);

    useEffect(() => {
        if (level === "project" && projectId && phaseData) {
            const phases = phaseData?.data ?? [];
            setPhaseOptions(
                phases.map((p) => ({
                    label: p.name || "",
                    value: String(p.id ?? ""),
                })),
            );
        }
    }, [level, projectId, phaseData]);



    const categoryOptions = useMemo(
        () => getFilterOptions("ARTEFACT_CATEGORY").map((o) => ({ value: o.key, label: o.display_text })),
        [getFilterOptions],
    );

    const subCategoryOptions = useMemo(() => {
        const all = getFilterOptions("ARTEFACT_SUB_CATEGORY");
        const filtered = artefactFilters.category.length > 0
            ? all.filter((o) => artefactFilters.category.includes(o.parent_key))
            : all;
        return filtered.map((o) => ({ value: o.key, label: o.display_text }));
    }, [getFilterOptions, artefactFilters.category]);
    const handleReset = () => {
        const empty = emptyArtefactsFilter();
        setArtefactFilters(empty);
        onApply(empty);
        onClose();
    };
    const handleApply = () => {
        onApply(artefactFilters);
        onClose();
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
            <Box
                flexGrow={1}
                sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2, overflowY: "auto" }}
            >
                {/* ── Programme-level: Artefact Type & related fields ─────── */}
                {level === "programme" && (
                    <>
                        <FieldControl
                            keyName="artefactType"
                            label="Artefact Type"
                            variant="select"
                            options={[
                                { value: "Project", label: "Project" },
                                { value: "Portfolio", label: "Portfolio" },
                            ]}
                            value={artefactFilters.artefactType}
                            onChange={(_, val) => {
                                set("artefactType", val as string);
                                set("portfolio", []);
                                set("project", []);
                            }}
                        />

                        {/* ── Show portfolio search when "Portfolio" type selected ── */}
                        {artefactFilters.artefactType === "Portfolio" && (
                            <ControlledAutocomplete
                                type="PORTFOLIO"
                                label="Search portfolio"
                                value={artefactFilters.portfolio}
                                multiSelect
                                onSelect={(val) => set("portfolio", (Array.isArray(val) ? val : val ? [val] : []) as AutocompleteOption[])}
                            />
                        )}
                        {/* ── Show project search when "Project" type selected ── */}
                        {artefactFilters.artefactType === "Project" && (
                            <AsyncMultiAutoComplete
                                label="Search project"
                                value={artefactFilters.project}
                                options={projectOptions}
                                onSearch={handleProjectSearch}
                                loading={searchProjectsMutation.isPending}
                                onChange={(val) => set("project", val as ProjectOption[])}
                            />
                        )}

                    </>
                )}
                <Divider />
                <AsyncMultiAutoComplete
                    label="Owner"
                    value={artefactFilters.owner}
                    options={ownerOptions}
                    onSearch={setOwnerSearch}
                    onScrollEnd={() => {
                        if (hasMoreOwners && !isFetchingMoreOwners) fetchNextOwners();
                    }}
                    loading={isFetchingMoreOwners}
                    onChange={(val) => set("owner", val as OwnerOption[])}
                />
                <FieldControl
                    keyName="category"
                    label="Category"
                    variant="select"
                    options={categoryOptions}
                    value={artefactFilters.category}
                    onChange={(_, val) => {
                        set("category", val as string[]);
                        set("subCategory", []);
                    }}
                    multiSelect
                />
                <FieldControl
                    keyName="subCategory"
                    label="Sub Category"
                    variant="select"
                    options={subCategoryOptions}
                    value={artefactFilters.subCategory}
                    onChange={(_, val) => set("subCategory", val as string[])}
                    multiSelect
                    disabled={artefactFilters.category.length === 0 && subCategoryOptions.length === 0}
                />

                <FieldControl
                    keyName="Required Date "
                    label="Required Date "
                    variant="date"
                    value={artefactFilters.requiredDate}
                    onChange={(_, val) => set("requiredDate", val )}
                    maxDate={artefactFilters.expiryDate ?? undefined}
                />
                <FieldControl
                    keyName="Expiry Date"
                    label="Expiry Date"
                    variant="date"
                    value={artefactFilters.expiryDate}
                    onChange={(_, val) => set("expiryDate", val )}
                    minDate={artefactFilters.requiredDate ?? undefined}
                />
            </Box>
            <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
                flexShrink={0}
                bgcolor="action.hover"
                p={2}
            >
                <Button variant="outlined" onClick={handleReset}>
                    Reset
                </Button>
                <Button variant="contained" onClick={handleApply}>
                    Apply
                </Button>
            </Stack>
        </Box>
    );
};
